import React, { useCallback, useEffect, useState } from "react";
import { Mail, MailOpen, ChevronDown, CheckCircle2, XCircle } from "lucide-react";
import { useRedirect } from "../../../Shared/Hooks/useRedirect ";
import { JobInvitationResponse } from "../../../InvitatationSystem/Types/InvitationSystemType";
import { getCandidateInvitations } from "../Services/CandidateIntelligenceService";
import { Section, EmptyState, formatRelative } from "./Section";

type StatusTab = "ALL" | "PENDING" | "ACCEPTED" | "REJECTED" | "EXPIRED";

const TAB_META: { key: StatusTab; label: string; badgeCls: string }[] = [
  { key: "ALL", label: "All", badgeCls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  { key: "PENDING", label: "Invited", badgeCls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { key: "ACCEPTED", label: "Active", badgeCls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { key: "REJECTED", label: "Declined", badgeCls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
  { key: "EXPIRED", label: "Expired", badgeCls: "bg-slate-700/60 text-slate-400 border-slate-600" },
];

interface InvitationCenterProps {
  refreshKey: number;
  onStatusChanged: () => void;
}

export const InvitationCenter: React.FC<InvitationCenterProps> = ({ refreshKey, onStatusChanged }) => {
  const redirect = useRedirect();
  const [invitations, setInvitations] = useState<JobInvitationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState<StatusTab>("ALL");
  const [visible, setVisible] = useState(3);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getCandidateInvitations("ALL", 0, 50);
      if (res.success && res.data) {
        setInvitations(res.data.content ?? []);
      } else if (res.message) {
        setError(res.message);
      }
    } catch (err) {
      setError("Invitations could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const counts = React.useMemo(() => {
    const c: Record<StatusTab, number> = { ALL: invitations.length, PENDING: 0, ACCEPTED: 0, REJECTED: 0, EXPIRED: 0 };
    for (const i of invitations) {
      const s = i.invitationStatus?.toUpperCase() as StatusTab;
      if (s === "PENDING" || s === "ACCEPTED" || s === "REJECTED" || s === "EXPIRED") c[s] += 1;
    }
    return c;
  }, [invitations]);

  const filtered = active === "ALL" ? invitations : invitations.filter((i) => (i.invitationStatus?.toUpperCase() ?? "PENDING") === active);
  const cards = filtered.slice(0, visible);

  const statusBadge = (status: string | null) => {
    const s = status?.toUpperCase();
    const meta = TAB_META.find((t) => t.key === s);
    if (!meta) return { label: "Invited", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
    return { label: meta.label, cls: meta.badgeCls };
  };

  return (
    <Section
      icon={<Mail size={17} />}
      title="Invitation Center"
      subtitle="Recruiter invitations tied to your profile"
      right={loading ? <span className="animate-pulse text-xs text-slate-500">Syncing…</span> : null}
    >
      {/* Status tabs */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {TAB_META.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setActive(t.key);
              setVisible(3);
            }}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
              active === t.key ? `${t.badgeCls} ring-1 ring-current` : "border-slate-700 bg-slate-900/50 text-slate-400 hover:text-slate-200"
            }`}
          >
            {t.label} <span className="opacity-70">({counts[t.key]})</span>
          </button>
        ))}
      </div>

      {error ? <p className="mb-3 text-xs font-semibold text-rose-300">{error}</p> : null}

      <div className="flex flex-col gap-2">
        {cards.length === 0 ? (
          <EmptyState label="No invitations yet — recruiters who invite you will show up here" />
        ) : (
          cards.map((inv) => {
            const st = statusBadge(inv.invitationStatus);
            return (
              <article
                key={inv.invitationId}
                className="flex items-start justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-3.5 transition hover:border-cyan-500/40"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold text-cyan-300">
                      {inv.companyName}
                    </span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>{st.label}</span>
                  </div>
                  <p className="mt-1.5 truncate text-sm font-bold text-slate-100">{inv.jobTittle}</p>
                  <p className="truncate text-xs text-slate-400">
                    {[inv.industry, ...(inv.location?.length ? inv.location : [])].filter(Boolean).join(" · ")}
                  </p>
                  {inv.message ? <p className="mt-1.5 truncate text-xs italic text-slate-500">“{inv.message}”</p> : null}
                  <p className="mt-1 text-[11px] text-slate-600">
                    {inv.invitationCreatedAt ? formatRelative(inv.invitationCreatedAt) : ""}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => redirect(`/candidate/dashboard/${inv.invitationId}`)}
                    className="rounded-lg bg-cyan-500/15 px-3 py-1.5 text-xs font-bold text-cyan-300 transition hover:bg-cyan-500/25"
                  >
                    View
                  </button>
                  {inv.invitationStatus?.toUpperCase() === "PENDING" ? (
                    <>
                      <button
                        type="button"
                        className="flex items-center gap-1 rounded-lg bg-emerald-500/15 px-3 py-1.5 text-[11px] font-bold text-emerald-300 transition hover:bg-emerald-500/25"
                      >
                        <CheckCircle2 size={13} /> Accept
                      </button>
                      <button
                        type="button"
                        className="flex items-center gap-1 rounded-lg bg-rose-500/15 px-3 py-1.5 text-[11px] font-bold text-rose-300 transition hover:bg-rose-500/25"
                      >
                        <XCircle size={13} /> Decline
                      </button>
                    </>
                  ) : null}
                </div>
              </article>
            );
          })
        )}
      </div>

      {filtered.length > visible ? (
        <button
          type="button"
          onClick={() => setVisible((v) => v + 3)}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-800 bg-slate-900/40 py-2 text-xs font-semibold text-slate-400 transition hover:text-cyan-300"
        >
          <ChevronDown size={14} /> View more invitations
        </button>
      ) : null}

      <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-900/40 px-3 py-2 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5">
          <MailOpen size={13} className="text-cyan-400" />
          {loading ? "Refreshing…" : `${invitations.length} total invitation${invitations.length === 1 ? "" : "s"}`}
        </span>
        <button
          type="button"
          onClick={() => {
            onStatusChanged();
          }}
          className="font-semibold text-cyan-400 hover:underline"
        >
          Refresh
        </button>
      </div>
    </Section>
  );
};